<template>
    <div class="w-full">
        <select
            v-if="stakeholderId"
            :id="id"
            v-model="modelValue"
            class="forms__input forms__input--base w-full"
            :disabled="disabled || logoOptions.length === 0"
        >
            <option :value="null">-- Vyberte logo --</option>
            <option v-for="logo in logoOptions" :key="logo.id" :value="logo.id">
                {{ logo.is_featured ? '★ ' : '' }}Logo #{{ logo.id }}
            </option>
        </select>
        <div v-else class="text-sm text-gray-400 italic py-2">Nejprve vyberte stakeholdera</div>

        <div v-if="selectedLogo" class="mt-2">
            <img :src="selectedLogo.url" alt="Logo preview" class="max-h-12 object-contain" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref } from 'vue';
import axios from 'axios';
import type { FieldComponent } from '@src/Common/Components/types';

interface Logo {
    id: number;
    url: string;
    is_featured: boolean;
}

interface Stakeholder {
    id: number;
    name: string;
    type: string;
    website: string;
    logos: Logo[];
}

const modelValue = defineModel({ type: [Number, String] });

const props = withDefaults(defineProps<FieldComponent>(), {
    disabled: false,
    id: null,
});

const stakeholders = ref<Map<number, Stakeholder>>(new Map());
const loaded = ref(false);

const stakeholderId = computed((): number | null => {
    if (!props.repeaterElement || !props.field) return null;

    const slug = props.field.stakeholderSlug ?? 'stakeholder';
    const value = props.repeaterElement[slug];

    if (!value) return null;

    // Handle array (relation field may store [{id, name}])
    if (Array.isArray(value)) {
        const first = value[0];
        return first?.id ? Number(first.id) : null;
    }

    // Handle object {id, name}
    if (typeof value === 'object' && value !== null && value.id) {
        return Number(value.id);
    }

    // Handle scalar
    if (typeof value === 'number' || typeof value === 'string') {
        return Number(value);
    }

    return null;
});

const logoOptions = computed((): Logo[] => {
    if (!stakeholderId.value || !stakeholders.value.has(stakeholderId.value)) {
        return [];
    }

    return stakeholders.value.get(stakeholderId.value)?.logos ?? [];
});

const selectedLogo = computed((): Logo | null => {
    if (!modelValue.value) {
        return null;
    }

    return logoOptions.value.find((l) => l.id === Number(modelValue.value)) ?? null;
});

// Auto-select featured logo when stakeholder changes
watch(stakeholderId, (newId, oldId) => {
    if (newId === oldId) {
        return;
    }

    if (!newId) {
        modelValue.value = null;
        return;
    }

    const logos = logoOptions.value;
    const featured = logos.find((l) => l.is_featured);
    modelValue.value = featured ? featured.id : (logos[0]?.id ?? null);
});

const fetchStakeholders = async () => {
    if (loaded.value || !props.field?.route) {
        return;
    }

    try {
        const response = await axios.get(props.field.route);
        const map = new Map<number, Stakeholder>();

        for (const s of response.data) {
            map.set(s.id, s);
        }

        stakeholders.value = map;
        loaded.value = true;
    } catch (e) {
        console.error('Failed to load stakeholders', e);
    }
};

onMounted(fetchStakeholders);
</script>
